import * as React from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import { isTypingTarget } from './grid-logic';
import { WorkspaceMenu, type WorkspaceMenuItem } from './menu';

/**
 * BulkBar — floating action bar shown while rows are selected (T-0140).
 *
 * Mirrors `.rq-bulk` in `docs/requirement-library/prototype/
 * selection.css`: an ink-900 bar centred near the bottom of the main
 * column with the selection count, an optional "select all N" link,
 * action buttons and a clear button. Renders nothing when `count` is 0.
 * An action with `menu` opens a `WorkspaceMenu` of choices instead of
 * running straight away (for example "Set criticality").
 *
 * Place it in `WorkspaceLayout`'s `overlay` slot (the main column is the
 * positioning context).
 *
 * Esc clears the selection, last: only when nothing else took the key (an
 * open side panel closes first and marks it handled, as do menus, dialogs
 * and inline edits), and never while typing in a field.
 */

export interface BulkBarAction {
  id: string;
  label: string;
  icon?: React.ReactNode;
  /** Runs on click. Not used when `menu` is given. */
  onClick?: () => void;
  /** Choices shown in a menu above the bar when the button is clicked. */
  menu?: { label?: React.ReactNode; items: ReadonlyArray<WorkspaceMenuItem> };
  disabled?: boolean;
}

export interface BulkBarProps {
  /** Number of selected rows. The bar is hidden at 0. */
  count: number;
  /** Singular noun for the count, for example "requirement". Default "row". */
  noun?: string;
  /**
   * Shows "Select all N" when fewer than `total` are selected.
   */
  selectAll?: { total: number; onSelectAll: () => void };
  actions: ReadonlyArray<BulkBarAction>;
  onClear: () => void;
  className?: string;
}

const BAR_BUTTON =
  'inline-flex h-[30px] items-center gap-1.5 rounded-chip px-2.5 text-meta font-medium text-paper-0 hover:bg-ink-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-500 disabled:cursor-not-allowed disabled:opacity-50';

export function BulkBar({
  count,
  noun = 'row',
  selectAll,
  actions,
  onClear,
  className,
}: BulkBarProps) {
  const onClearRef = React.useRef(onClear);
  onClearRef.current = onClear;
  const shown = count > 0;

  React.useEffect(() => {
    if (!shown) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      if (document.querySelector('[role="dialog"][data-state="open"]')) return;
      if (isTypingTarget(e.target)) return;
      e.preventDefault();
      onClearRef.current();
    };
    // On `window`, which a key reaches after every listener on `document`
    // (the side panel's among them), so their Esc comes first.
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [shown]);

  if (!shown) return null;
  return (
    <div
      role="toolbar"
      aria-label="Bulk actions"
      className={cn(
        'absolute bottom-12 left-1/2 z-20 flex -translate-x-1/2 items-center gap-1.5 whitespace-nowrap rounded-large bg-ink-900 py-1.5 pl-3.5 pr-1.5 text-body-sm text-paper-0 shadow-modal',
        className,
      )}
    >
      <span className="mr-1.5 font-semibold" aria-live="polite">
        {count.toLocaleString()} {count === 1 ? noun : `${noun}s`} selected
      </span>
      {selectAll && count < selectAll.total ? (
        <button
          type="button"
          onClick={selectAll.onSelectAll}
          className={cn(BAR_BUTTON, 'text-ink-200 underline underline-offset-[3px]')}
        >
          Select all {selectAll.total.toLocaleString()}
        </button>
      ) : null}
      <span className="h-[18px] w-px bg-ink-700" aria-hidden="true" />
      {actions.map((a) =>
        a.menu ? (
          <WorkspaceMenu
            key={a.id}
            aria-label={a.label}
            label={a.menu.label}
            items={a.menu.items}
            side="top"
            trigger={
              <button type="button" disabled={a.disabled} className={BAR_BUTTON}>
                {a.icon}
                {a.label}
              </button>
            }
          />
        ) : (
          <button
            key={a.id}
            type="button"
            onClick={a.onClick}
            disabled={a.disabled}
            className={BAR_BUTTON}
          >
            {a.icon}
            {a.label}
          </button>
        ),
      )}
      <span className="h-[18px] w-px bg-ink-700" aria-hidden="true" />
      <button type="button" aria-label="Clear selection" onClick={onClear} className={BAR_BUTTON}>
        <X className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
    </div>
  );
}

BulkBar.displayName = 'BulkBar';
